// Calendar — three views (Week / Day / Month) over personal events.
//
// Phase 1: list + create + edit + delete. Skipped for Phase 2:
// reminders/alarms, recurring events (RRULE), shared calendars +
// invites, drag-drop reschedule.

import { useEffect, useMemo, useState } from "preact/hooks";
import "../../styles/calendar.css";

import { Icon } from "../../icons";
import { useCalendar } from "../../lib/use-calendar";
import { useCalendars } from "../../lib/use-calendars";
import { useEventDrag } from "../../lib/use-event-drag";
import type { CalendarEvent } from "../../lib/rpc";
import { MONTH_NAMES, allDayIso, isoAtHour, parseDateOnly as parseDate, sameDay, weekStart } from "../../lib/calendar-utils";

import { WeekView } from "./week";
import { DayView } from "./day";
import { MonthView } from "./month";
import { EventDetail } from "./event-detail";
import { NewEventModal, type NewEventPrefill } from "./new-event";
import { ManageCalendars } from "./manage-calendars";

interface CalendarViewProps {
  notify: (msg: string) => void;
  meId: string | null;
}

export function CalendarView({ notify, meId }: CalendarViewProps) {
  const c = useCalendar();
  const cals = useCalendars();
  const [selected, setSelected] = useState<CalendarEvent | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [newPrefill, setNewPrefill] = useState<NewEventPrefill | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  // Filter — "all" (default) shows every visible event; "personal" hides
  // shared-calendar events; a specific calendar id narrows to that one.
  const [calFilter, setCalFilter] = useState<"all" | "personal" | string>("all");
  // Pointer-based drag controller. Drops in week/day land on hour
  // columns (data-drop-hour-col); drops in week-all-day strip + month
  // cells land on day targets (data-drop-day). The controller wires
  // window-level pointermove/up so absolute-positioned events don't
  // intercept the drop the way they did with HTML5 dragover.
  const drag = useEventDrag({
    onDropAtHour: async (evt, day, hour) => {
      if (evt.allDay) return;
      const oldStart = new Date(evt.dtstart);
      const oldEnd = new Date(evt.dtend);
      const durationMs = oldEnd.getTime() - oldStart.getTime();
      const newStart = new Date(isoAtHour(day, hour));
      if (newStart.getTime() === oldStart.getTime()) return;
      const newEnd = new Date(newStart.getTime() + durationMs);
      await c.updateEvent(evt.id, {
        dtstart: newStart.toISOString(),
        dtend: newEnd.toISOString(),
      });
    },
    onDropAtDay: async (evt, day) => {
      if (evt.allDay) {
        const startD = parseDate(evt.dtstart);
        const endD = parseDate(evt.dtend);
        if (sameDay(day, startD)) return;
        const dayDeltaMs = day.getTime() - startD.getTime();
        const newEndD = new Date(endD.getTime() + dayDeltaMs);
        await c.updateEvent(evt.id, {
          dtstart: allDayIso(day),
          dtend: allDayIso(newEndD),
        });
        return;
      }
      const oldStart = new Date(evt.dtstart);
      const oldEnd = new Date(evt.dtend);
      if (sameDay(day, oldStart)) return;
      const durationMs = oldEnd.getTime() - oldStart.getTime();
      const newStart = new Date(day);
      newStart.setHours(oldStart.getHours(), oldStart.getMinutes(), oldStart.getSeconds(), 0);
      const newEnd = new Date(newStart.getTime() + durationMs);
      await c.updateEvent(evt.id, {
        dtstart: newStart.toISOString(),
        dtend: newEnd.toISOString(),
      });
    },
  });

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  useEffect(() => { if (c.error) notify(c.error); }, [c.error, notify]);
  useEffect(() => { if (cals.error) notify(cals.error); }, [cals.error, notify]);

  // Apply the calendar filter to the event stream once. All downstream
  // views receive the filtered list — they don't need to know about
  // the filter mode.
  //
  // String-coerced equality on calendarId: the server's calendar_id
  // column is INTEGER and the row mapper passes it through unchanged,
  // so events arrive with `calendarId: 1` (number). The <select>
  // dropdown's value is always a string ("1"). Triple-equals between a
  // number and a string is always false — and that silently turns the
  // "Bug Test Shared" filter into an empty calendar for the wife.
  // Coerce both sides so the comparison works regardless of how the
  // value flowed in. The TS types still claim string, which is a
  // separate honesty problem worth a follow-up but doesn't block here.
  const visibleEvents = useMemo(() => {
    if (calFilter === "all") return c.events;
    if (calFilter === "personal") return c.events.filter((e) => !e.calendarId);
    return c.events.filter((e) => e.calendarId != null && String(e.calendarId) === String(calFilter));
  }, [c.events, calFilter]);

  const headerTitle = useMemo(() => {
    if (c.view === "day") {
      return c.focusDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
    }
    if (c.view === "week") {
      const wkStart = weekStart(c.anchor);
      const wkEnd = new Date(wkStart);
      wkEnd.setDate(wkEnd.getDate() + 6);
      if (wkStart.getMonth() === wkEnd.getMonth()) {
        return `${MONTH_NAMES[wkStart.getMonth()]} ${wkStart.getFullYear()}`;
      }
      return `${MONTH_NAMES[wkStart.getMonth()].slice(0, 3)} – ${MONTH_NAMES[wkEnd.getMonth()].slice(0, 3)} ${wkEnd.getFullYear()}`;
    }
    return `${MONTH_NAMES[c.anchor.getMonth()]} ${c.anchor.getFullYear()}`;
  }, [c.view, c.anchor, c.focusDate]);

  // Navigation — meaning differs per view. Week jumps a week, day jumps
  // a day (and keeps anchor in sync so the range query refetches when
  // we cross months), month jumps a calendar month.
  const shift = (direction: -1 | 1) => {
    if (c.view === "week") {
      const next = new Date(c.anchor);
      next.setDate(next.getDate() + 7 * direction);
      c.setAnchor(next);
    } else if (c.view === "day") {
      const next = new Date(c.focusDate);
      next.setDate(next.getDate() + direction);
      c.setFocusDate(next);
      c.setAnchor(next);
    } else {
      const next = new Date(c.anchor);
      next.setMonth(next.getMonth() + direction);
      c.setAnchor(next);
    }
  };

  const goToday = () => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    c.setAnchor(t);
    c.setFocusDate(t);
  };

  const onTapEmpty = (day: Date, hour: number) => {
    setEditing(null);
    // If the user has a calendar filter active, default the new event
    // to that calendar so quick-creates stay in the visible scope.
    const defaultCal = calFilter !== "all" && calFilter !== "personal" ? calFilter : null;
    setNewPrefill({ day, hour, calendarId: defaultCal });
    setNewOpen(true);
  };

  const onJumpDay = (d: Date) => {
    c.setFocusDate(d);
    c.setAnchor(d);
    c.setView("day");
  };

  const onSelect = (evt: CalendarEvent) => { setSelected(evt); };


  const onEditFromDetail = () => {
    if (!selected) return;
    setEditing(selected);
    setNewPrefill(null);
    setNewOpen(true);
    setSelected(null);
  };

  const onRemoveSelected = async () => {
    if (!selected) return;
    const ok = await c.deleteEvent(selected.id);
    if (ok) { notify("Event removed"); setSelected(null); }
  };

  const onCreateEvent = async (args: Parameters<typeof c.createEvent>[0]) => {
    const evt = await c.createEvent(args);
    if (evt) notify("Event added");
    return evt;
  };

  const onUpdateEvent = async (id: string, patch: Parameters<typeof c.updateEvent>[1]) => {
    const evt = await c.updateEvent(id, patch);
    if (evt) notify("Event updated");
    return evt;
  };

  return (
    <div className="cal-shell">
      <div className="cal-head">
        <div className="cal-head-left">
          <h1 className="cal-head-title">{headerTitle}</h1>
          <div style={{ display: "flex", gap: 4 }}>
            <button className="icon-btn" onClick={() => shift(-1)} title="Previous" type="button">
              <Icon name="arrow-left" />
            </button>
            <button className="icon-btn" onClick={() => shift(1)} title="Next" type="button">
              <Icon name="arrow-right" />
            </button>
          </div>
          <button className="btn cal-today-btn" onClick={goToday} type="button" data-active={sameDay(c.view === "day" ? c.focusDate : c.anchor, today)}>
            Today
          </button>
        </div>
        <div className="cal-head-right">
          <select
            className="cal-filter"
            value={calFilter}
            onChange={(e) => setCalFilter((e.currentTarget as HTMLSelectElement).value as any)}
            title="Filter by calendar"
          >
            <option value="all">All calendars</option>
            <option value="personal">Personal only</option>
            {cals.calendars.length > 0 && <option disabled>──────────</option>}
            {cals.calendars.map((cc) => (
              <option key={cc.id} value={cc.id}>{cc.name}</option>
            ))}
          </select>
          <button
            className="icon-btn"
            onClick={() => setManageOpen(true)}
            title="Manage calendars"
            type="button"
          >
            <Icon name="people" size={14} />
          </button>
          <div className="pill-mini cal-view-pill">
            <button data-active={c.view === "week"}  onClick={() => c.setView("week")}  type="button">Week</button>
            <button data-active={c.view === "day"}   onClick={() => c.setView("day")}   type="button">Day</button>
            <button data-active={c.view === "month"} onClick={() => c.setView("month")} type="button">Month</button>
          </div>
          <button
            className="btn btn-primary cal-new-btn"
            onClick={() => {
              setEditing(null);
              const defaultCal = calFilter !== "all" && calFilter !== "personal" ? calFilter : null;
              setNewPrefill(defaultCal ? { calendarId: defaultCal } : null);
              setNewOpen(true);
            }}
            type="button"
          >
            <Icon name="plus" size={14} /> <span className="cal-new-label">New event</span>
          </button>
        </div>
      </div>

      {c.view === "week"  && <WeekView  anchor={c.anchor} today={today} events={visibleEvents} calendars={cals.calendars} onSelect={onSelect} onTapEmpty={onTapEmpty} onJumpDay={onJumpDay} drag={drag} />}
      {c.view === "day"   && <DayView   focusDate={c.focusDate} setFocusDate={c.setFocusDate} today={today} events={visibleEvents} calendars={cals.calendars} onSelect={onSelect} onTapEmpty={onTapEmpty} drag={drag} />}
      {c.view === "month" && <MonthView anchor={c.anchor} today={today} events={visibleEvents} calendars={cals.calendars} onSelect={onSelect} onTapDay={onJumpDay} drag={drag} />}

      {selected && (
        <EventDetail
          event={selected}
          calendars={cals.calendars}
          meId={meId}
          notify={notify}
          onClose={() => setSelected(null)}
          onEdit={onEditFromDetail}
          onRemove={onRemoveSelected}
          onChanged={() => c.refresh()}
        />
      )}

      <NewEventModal
        open={newOpen}
        editing={editing}
        prefill={newPrefill}
        weekAnchor={c.anchor}
        calendars={cals.calendars}
        onClose={() => { setNewOpen(false); setEditing(null); }}
        onCreate={onCreateEvent}
        onUpdate={onUpdateEvent}
      />

      <ManageCalendars
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        cals={cals}
        notify={notify}
      />
    </div>
  );
}
